import { TABS } from "@/react/features/support-tools/dialplans/constants";
import { useSelector } from "react-redux";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import React, { useEffect, useState } from "react";
import {
  useCreateDialplanMutation,
  useLazyGetDialplansQuery,
} from "@/react/services/common/customerDialplansApi";
import styled from "styled-components";
import Tabs, { Tab } from "@8x8/oxygen-tabs";
import { CustomerCard } from "@/react/features/customer";
import PageLayout from "@/react/features/styled/PageLayout";
import { usePermissions } from "@/react/services/rbac/usePermissions";
import { GLOBAL_DIALPLANS_WRITE } from "@/react/services/rbac/permissions";
import DialplansList from "@/react/features/support-tools/dialplans/DialplansList";
import {
  useCreateGlobalDialplanMutation,
  useLazyGetGlobalDialplansQuery,
} from "@/react/features/support-tools/dialplans/globalDialplansApi";

const StyledTabs = styled(Tabs)`
  margin: 0 0 16px;
  overflow-x: unset;
`;

const DialplansPage = () => {
  const [createCustomerDialplanApi, { isLoading: isCreatingCustomerDialplan }] =
    useCreateDialplanMutation();
  const [createGlobalDialplanApi, { isLoading: isCreatingGlobalDialplan }] =
    useCreateGlobalDialplanMutation();
  const [
    fetchCustomerDialplans,
    {
      isFetching: isFethingCustomerDialplans,
      error: customerDialplansError,
      refetch,
    },
  ] = useLazyGetDialplansQuery();
  const [
    fetchGlobalDialplans,
    { isFetching: isFethingGlobalDialplans, error: globalDialplansError },
  ] = useLazyGetGlobalDialplansQuery();

  const [tabSelected, setTabSelected] = useState(TABS.CUSTOMER);
  const [dialplans, setDialplans] = useState([]);
  const { hasPermission } = usePermissions();

  const customerId = useSelector(customerIdSelector);
  useEffect(() => {
    refetch && refetch();
  }, [customerId]);

  useEffect(() => {
    if (tabSelected === TABS.CUSTOMER) {
      fetchCustomerDialplans().then((result) => {
        setDialplans(result.data);
      });
    } else if (hasPermission(GLOBAL_DIALPLANS_WRITE)) {
      fetchGlobalDialplans().then((result) => {
        setDialplans(result.data);
      });
    }
  }, [tabSelected, fetchGlobalDialplans, fetchCustomerDialplans]);

  const isGlobalTabWithAccess =
    hasPermission(GLOBAL_DIALPLANS_WRITE) && tabSelected === TABS.GLOBAL;

  return (
    <PageLayout>
      {tabSelected === TABS.CUSTOMER && <CustomerCard />}
      {hasPermission(GLOBAL_DIALPLANS_WRITE) && (
        <StyledTabs>
          {Object.values(TABS).map((tabName) => (
            <Tab
              key={tabName}
              color="light"
              onClick={() => setTabSelected(tabName)}
              isActive={tabSelected === tabName}
            >
              <h6>{tabName}</h6>
            </Tab>
          ))}
        </StyledTabs>
      )}
      <DialplansList
        dialplans={dialplans}
        createDialplanApi={
          isGlobalTabWithAccess
            ? createGlobalDialplanApi
            : createCustomerDialplanApi
        }
        isCreating={
          isGlobalTabWithAccess
            ? isCreatingGlobalDialplan
            : isCreatingCustomerDialplan
        }
        fetchingError={
          isGlobalTabWithAccess ? globalDialplansError : customerDialplansError
        }
        isFetching={
          isGlobalTabWithAccess
            ? isFethingGlobalDialplans
            : isFethingCustomerDialplans
        }
        tab={tabSelected}
      />
    </PageLayout>
  );
};

export default DialplansPage;
